import { useState } from 'react';
import { ChevronDown, ChevronRight, Layers, Sparkles } from 'lucide-react';
import type { AlignerShellRequest } from '@export/aligner/AlignerShellGenerator';
import type { AlignerStatus } from '@export/store/exportStore.ts';
import useExportStore from '@export/hooks/useExportStore';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

type Props = {
  generateAlignerShell: ((options: AlignerShellRequest) => void) | null;
  status: AlignerStatus;
};

const defaultConfig: AlignerShellRequest = {
  jaw: 'lower',
  shellOffset: 0.25,
  voxelSize: 0.3,
  thickness: 0.8,
  blockoutStrength: 0,
  debugStage: 'final',
  trimRimClamp: true,
};

type SliderFieldKey = 'shellOffset' | 'thickness';
const sliderFields: Array<{
  key: SliderFieldKey;
  label: string;
  min: number;
  max: number;
  step: number;
  suffix?: string;
}> = [
  { key: 'shellOffset', label: 'Shell offset', min: 0.1, max: 1, step: 0.05, suffix: 'mm' },
  { key: 'thickness', label: 'Wall thickness', min: 0.1, max: 2, step: 0.05, suffix: 'mm' },
];

export function AlignerPanel({ generateAlignerShell, status }: Readonly<Props>) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<AlignerShellRequest>(defaultConfig);
  const { trimCurveState } = useExportStore();
  const isPending = status.state === 'pending';
  const trimReady = trimCurveState === 'ready';

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!generateAlignerShell || isPending || !trimReady) return;
    generateAlignerShell(form);
  };

  return (
    <div className="rounded-3xl border border-white/10 bg-black/30 p-4 text-white backdrop-blur">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-2"
      >
        <span className="flex items-center gap-2 text-sm font-semibold">
          <Layers className="h-4 w-4 text-primary-300" />
          Aligner
        </span>
        {open ? <ChevronDown className="h-4 w-4 text-white/60" /> : <ChevronRight className="h-4 w-4 text-white/60" />}
      </button>

      {open && (
        <form onSubmit={handleSubmit} className="mt-4 flex flex-col gap-4">
          <div>
            <Label>Jaw selection</Label>
            <Select
              value={form.jaw}
              onValueChange={(value) => setForm((prev) => ({ ...prev, jaw: value as AlignerShellRequest['jaw'] }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Choose jaw" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="both">Upper &amp; Lower</SelectItem>
                <SelectItem value="upper">Upper only</SelectItem>
                <SelectItem value="lower">Lower only</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {sliderFields.map(({ key, label, min, max, step, suffix }) => (
            <div key={key} className="flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <Label>{label}</Label>
                <span className="text-xs text-white/70">
                  {form[key].toFixed(2)}
                  {suffix ? ` ${suffix}` : ''}
                </span>
              </div>
              <Slider
                min={min}
                max={max}
                step={step}
                value={[form[key]]}
                onValueChange={([value]) => setForm((prev) => ({ ...prev, [key]: value }))}
              />
            </div>
          ))}

          <label className="flex items-start gap-2 text-xs text-white/55">
            <Checkbox
              className="mt-0.5"
              checked={form.trimRimClamp ?? true}
              onCheckedChange={(value) => setForm((prev) => ({ ...prev, trimRimClamp: value === true }))}
            />
            <span>
              Snap rim to trim curve
              <span className="block text-white/40">
                Pulls the extracted rim exactly onto the curve. Off = raw voxel-resolution edge.
              </span>
            </span>
          </label>

          <Button type="submit" className="w-full" disabled={!generateAlignerShell || isPending || !trimReady}>
            <Sparkles />
            {isPending ? 'Generating…' : trimReady ? 'Generate aligner' : 'Preparing trim curve…'}
          </Button>

          {status.state === 'error' && (
            <div className="rounded-2xl border border-rose-400/30 bg-rose-400/10 px-3 py-2 text-xs text-rose-100">
              <p className="text-sm font-medium text-rose-300">Aligner failed</p>
              <p>{status.message}</p>
            </div>
          )}
        </form>
      )}
    </div>
  );
}
